import React from 'react';
import { useFela } from 'react-fela';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import * as styles from './ArticleList.style';
import { Box } from './generic/box/box';
import { sortByTitleWeighted } from 'src/utils/sorting';

import type { Theme } from '../types';

export interface ShortArticle {
  id: string;
  title: string;
  parentIds?: number[];
  childIds?: number[];
  weight: number;
}

interface Props {
  // The view these articles were loaded for. Taken from the caller rather than
  // from the current route, so the links match the rows on screen even while
  // another page is loading.
  viewHash: string;
  articles: ShortArticle[];
  isSearch?: boolean;
}

const ArticleList = ({ viewHash, articles, isSearch = false }: Props): React.ReactElement | null => {
  const { theme } = useFela<Theme>();
  const { t, i18n } = useTranslation();

  // An empty list would still paint the styled container, leaving a bare white
  // box on the page.
  if (articles.length === 0) {
    return null;
  }

  return (
    <Box as="ul" style={styles.list()} className="w-full basis-1/4 mr-5">
      {sortByTitleWeighted(articles, i18n.language).map((article) => (
        // The row is the list item and the link sits inside it, so the list has
        // no anchor as a direct child. The view stylesheet indents `li`, which
        // is why the padding stays on this element rather than on the link.
        <Box
          as="li"
          key={`article-list-item-${article.id}`}
          style={styles.row(theme)}
          className="align-middle cursor-pointer border-b px-5 py-2 align-center hover:bg-gray-100"
        >
          <Link to={`/v/${viewHash}/a/${article.id}`} className="kb-article-row__link">
            <Box as="span" style={styles.icon} className="kb-article-row__icon">
              <i className="fa fa-file-alt" />
            </Box>
            <Box as="span" style={styles.title} className="kb-article-row__title">
              {article.title}
              {isSearch && (
                <>
                  {!!article.childIds?.length && <span className="search__label">{t('search.labels.parent')}</span>}
                  {!!article.parentIds?.length && <span className="search__label">{t('search.labels.child')}</span>}
                </>
              )}
            </Box>
            <Box as="span" style={styles.chevron} className="kb-article-row__chevron">
              <i className="fa fa-chevron-right" />
            </Box>
          </Link>
        </Box>
      ))}
    </Box>
  );
};

export default ArticleList;
